import React, { useState, useEffect } from 'react';
import {
  History,
  User,
  Bot,
  Key,
  Webhook,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  Globe,
  Terminal,
  AlertCircle,
  CheckCircle,
  XCircle,
  Search
} from 'lucide-react';
import { auditService } from 'api/services/audit.service';
import { AuditLog, AuditLogFilters, AuditLogResponse, ActorType, ActionType, TriggerSource } from 'types';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const ITEMS_PER_PAGE = 50;

// Helper to format date
const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Actor type icons and colors
const getActorIcon = (actorType: ActorType) => {
  switch (actorType) {
    case 'USER': return <User className="w-4 h-4" />;
    case 'SYSTEM_JOB': return <Bot className="w-4 h-4" />;
    case 'API_KEY': return <Key className="w-4 h-4" />;
    case 'WEBHOOK': return <Webhook className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

const getActorColor = (actorType: ActorType) => {
  switch (actorType) {
    case 'USER': return 'bg-blue-100 text-blue-800';
    case 'SYSTEM_JOB': return 'bg-purple-100 text-purple-800';
    case 'API_KEY': return 'bg-amber-100 text-amber-800';
    case 'WEBHOOK': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Action type colors
const getActionColor = (actionType: ActionType) => {
  switch (actionType) {
    case 'CREATE': return 'bg-green-100 text-green-800';
    case 'UPDATE': return 'bg-blue-100 text-blue-800';
    case 'DELETE': return 'bg-red-100 text-red-800';
    case 'LOGIN': return 'bg-emerald-100 text-emerald-800';
    case 'LOGOUT': return 'bg-gray-100 text-gray-800';
    case 'LOGIN_FAILED': return 'bg-red-100 text-red-800';
    case 'STATUS_CHANGE': return 'bg-yellow-100 text-yellow-800';
    case 'ACTIVATE': return 'bg-green-100 text-green-800';
    case 'PAUSE': return 'bg-orange-100 text-orange-800';
    case 'RESUME': return 'bg-teal-100 text-teal-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Status icons
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'SUCCESS': return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'FAILURE': return <XCircle className="w-4 h-4 text-red-500" />;
    case 'PARTIAL': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    default: return <CheckCircle className="w-4 h-4 text-gray-500" />;
  }
};

// Trigger source icons
const getTriggerIcon = (source: TriggerSource) => {
  switch (source) {
    case 'WEB_UI': return <Globe className="w-3 h-3" />;
    case 'REST_API': return <Terminal className="w-3 h-3" />;
    case 'SCHEDULED_JOB': return <Clock className="w-3 h-3" />;
    case 'WEBHOOK': return <Webhook className="w-3 h-3" />;
    default: return <Terminal className="w-3 h-3" />;
  }
};

// Diff viewer component for before/after values
const DiffViewer: React.FC<{ oldValues?: Record<string, any>; newValues?: Record<string, any>; changedFields?: string[] }> = ({
  oldValues,
  newValues,
  changedFields
}) => {
  if (!changedFields || changedFields.length === 0) {
    return <span className="text-gray-400 text-xs">No changes recorded</span>;
  }

  return (
    <div className="space-y-1 text-xs">
      {changedFields.slice(0, 5).map((field) => (
        <div key={field} className="flex items-start gap-2">
          <span className="font-medium text-gray-600 min-w-[80px]">{field}:</span>
          <div className="flex items-center gap-1">
            {oldValues?.[field] !== undefined && (
              <span className="bg-red-50 text-red-700 px-1 rounded line-through">
                {String(oldValues[field]).substring(0, 30)}
                {String(oldValues[field]).length > 30 && '...'}
              </span>
            )}
            <span className="text-gray-400">→</span>
            {newValues?.[field] !== undefined && (
              <span className="bg-green-50 text-green-700 px-1 rounded">
                {String(newValues[field]).substring(0, 30)}
                {String(newValues[field]).length > 30 && '...'}
              </span>
            )}
          </div>
        </div>
      ))}
      {changedFields.length > 5 && (
        <span className="text-gray-400">+{changedFields.length - 5} more fields</span>
      )}
    </div>
  );
};

// Expandable row component
const AuditLogRow: React.FC<{ log: AuditLog }> = ({ log }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50 transition cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <td className="px-4 py-3">
          <button className="text-gray-400 hover:text-gray-600">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {formatDateTime(log.createdAt)}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getActorColor(log.actorType)}`}>
              {getActorIcon(log.actorType)}
              {log.actorType}
            </span>
          </div>
          <div className="text-sm text-gray-600 mt-0.5">
            {log.actorName || log.actorId || 'Unknown'}
          </div>
          {log.actorRole && (
            <div className="text-xs text-gray-400">{log.actorRole}</div>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.actionType)}`}>
            {log.actionType}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-gray-900">{log.resourceType}</div>
          {log.resourceName && (
            <div className="text-xs text-gray-500">{log.resourceName}</div>
          )}
          {log.resourceId && (
            <div className="text-xs text-gray-400">ID: {log.resourceId}</div>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {getTriggerIcon(log.triggerSource)}
            {log.triggerSource}
          </div>
        </td>
        <td className="px-4 py-3">
          {getStatusIcon(log.status)}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={7} className="px-6 py-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Left column - Details */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Details</h4>
                <div className="text-sm space-y-1">
                  {log.actorIp && (
                    <div><span className="text-gray-500">IP Address:</span> {log.actorIp}</div>
                  )}
                  {log.apiEndpoint && (
                    <div>
                      <span className="text-gray-500">Endpoint:</span>{' '}
                      <code className="bg-gray-100 px-1 rounded text-xs">{log.httpMethod} {log.apiEndpoint}</code>
                    </div>
                  )}
                  {log.durationMs && (
                    <div><span className="text-gray-500">Duration:</span> {log.durationMs}ms</div>
                  )}
                  {log.errorMessage && (
                    <div className="text-red-600">
                      <span className="text-gray-500">Error:</span> {log.errorMessage}
                    </div>
                  )}
                </div>
              </div>
              {/* Right column - Changes */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Changes</h4>
                {log.changesSummary ? (
                  <div className="text-sm text-gray-600 bg-white p-2 rounded border">
                    {log.changesSummary}
                  </div>
                ) : (
                  <DiffViewer
                    oldValues={log.oldValues}
                    newValues={log.newValues}
                    changedFields={log.changedFields}
                  />
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditLogs: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    size: ITEMS_PER_PAGE
  });
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>('all');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [actorTypeFilter, setActorTypeFilter] = useState<string>('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('');
  const [triggerSourceFilter, setTriggerSourceFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const currentRole = (currentOrganization?.role || user?.role || '').toLowerCase();

  // Check access - only SUPER_ADMIN and SERVICE_ADMIN can view audit logs
  const canViewAuditLogs = currentRole === 'master_admin' ||
                           currentRole === 'super_admin' ||
                           currentRole === 'service_admin';

  // Determine organizationId to pass to API (consistent with other pages)
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      // Admins can view all or filter by specific org
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined; // No filter - view all
      }
      return selectedOrgId;
    }
    // Non-admins see only their organization
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError(null);

    const orgId = getOrganizationId();

    const searchFilters: AuditLogFilters = {
      page: currentPage,
      size: ITEMS_PER_PAGE,
      organizationId: orgId,
      actionType: actionTypeFilter as ActionType || undefined,
      actorType: actorTypeFilter as ActorType || undefined,
      resourceType: resourceTypeFilter || undefined,
      triggerSource: triggerSourceFilter as TriggerSource || undefined,
      startDate: dateFrom || undefined,
      endDate: dateTo || undefined
    };

    const response = await auditService.searchAuditLogs(searchFilters);

    if (response.success && response.data) {
      setData(response.data);
    } else {
      setError(response.error || 'Failed to load audit logs');
    }

    setLoading(false);
  };

  useEffect(() => {
    if (canViewAuditLogs) {
      fetchAuditLogs();
    }
  }, [currentPage, canViewAuditLogs, selectedOrgId]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchAuditLogs();
  };

  const handleClearFilters = () => {
    setSelectedOrgId('all');
    setActionTypeFilter('');
    setActorTypeFilter('');
    setResourceTypeFilter('');
    setTriggerSourceFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  if (!canViewAuditLogs) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <History className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
        <p className="text-gray-600 text-center max-w-md">
          You do not have permission to view Audit Logs. This feature is only available to Super Admins and Service Admins.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <History className="w-7 h-7" />
            Audit Logs
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Track all system activities and changes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={fetchAuditLogs}
            className="tl-btn tl-btn-primary"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Organization Filter - Always visible for admins (like other pages) */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              setSelectedOrgId(value || 'all');
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Action Type */}
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="LOGIN_FAILED">Login Failed</option>
              <option value="STATUS_CHANGE">Status Change</option>
              <option value="ACTIVATE">Activate</option>
              <option value="PAUSE">Pause</option>
            </select>

            {/* Actor Type */}
            <select
              value={actorTypeFilter}
              onChange={(e) => setActorTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Actors</option>
              <option value="USER">User</option>
              <option value="SYSTEM_JOB">System Job</option>
              <option value="API_KEY">API Key</option>
              <option value="WEBHOOK">Webhook</option>
            </select>

            {/* Resource Type */}
            <input
              type="text"
              placeholder="Resource type..."
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />

            {/* Trigger Source */}
            <select
              value={triggerSourceFilter}
              onChange={(e) => setTriggerSourceFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sources</option>
              <option value="WEB_UI">Web UI</option>
              <option value="REST_API">REST API</option>
              <option value="SCHEDULED_JOB">Scheduled Job</option>
              <option value="WEBHOOK">Webhook</option>
              <option value="AGI_SCRIPT">AGI Script</option>
            </select>

            {/* Date From */}
            <input
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="From date"
            />

            {/* Date To */}
            <input
              type="datetime-local"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="To date"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Clear Filters
            </button>
            <button
              onClick={handleApplyFilters}
              className="tl-btn tl-btn-primary"
            >
              <Search className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {data && (
        <div className="text-sm text-gray-600">
          Showing {data.logs.length} of {data.totalItems} audit logs
        </div>
      )}

      {/* Loading / Error States */}
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} onRetry={fetchAuditLogs} />}

      {/* Audit Logs Table */}
      {!loading && !error && data && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <History className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  data.logs.map((log) => (
                    <AuditLogRow key={log.id} log={log} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.logs.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={setCurrentPage}
                totalItems={data.totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
